import { Link } from "react-router-dom";
import useSWR from "swr";

import { ArtRow, CatCard, GuideCard, KbSearchBox } from "../components/KbCards";
import { features } from "../config/features";
import { api, type KbArticleListItem } from "../services/api";

/**
 * KB Browse / home: hero search, a guided-walkthrough spotlight, the category
 * grid, and a "popular" list. The self-service centrepiece (guided
 * walkthroughs) leads, mirroring the approved prototype.
 */
export function KbBrowse() {
  const { data: guides } = useSWR<KbArticleListItem[]>("/api/kb/guides", () => api.kb.guides());
  const { data: categories } = useSWR<string[]>("/api/kb/categories", () => api.kb.categories());
  const { data: page } = useSWR("/api/kb?popular", () =>
    api.kb.list({ page: 1, page_size: 8 }),
  );

  // No server-side popularity sort; rank the recent page by net helpful votes.
  const popular = [...(page?.items ?? [])]
    .sort((a, b) => b.thumbs_up - b.thumbs_down - (a.thumbs_up - a.thumbs_down))
    .slice(0, 6);

  return (
    <div className="kb-page">
      <div className="kb-hero">
        <h1 className="page-title">How can we help?</h1>
        <p className="page-subtitle">
          Search the knowledge base or browse a topic. Many issues you can fix yourself in a few
          minutes &mdash; our guided walkthroughs take you through it step by step.
        </p>
        <KbSearchBox />
      </div>

      {guides && guides.length > 0 && (
        <section className="kb-section">
          <div className="kb-section-head">
            <div className="kb-section-title">
              Guided walkthroughs<span className="sub">Fix it yourself, step by step</span>
            </div>
          </div>
          <div className="kb-guides">
            {guides.map((g) => (
              <GuideCard key={g.id} article={g} />
            ))}
          </div>
        </section>
      )}

      {categories && categories.length > 0 && (
        <section className="kb-section">
          <div className="kb-section-head">
            <div className="kb-section-title">Browse by topic</div>
            <Link className="link" to="/kb/categories">
              All categories
            </Link>
          </div>
          <div className="kb-cat-grid">
            {categories.map((c) => (
              <CatCard key={c} category={c} />
            ))}
          </div>
        </section>
      )}

      {popular.length > 0 && (
        <section className="kb-section">
          <div className="kb-section-head">
            <div className="kb-section-title">Popular right now</div>
          </div>
          <div className="kb-art-list">
            {popular.map((a) => (
              <ArtRow key={a.id} article={a} />
            ))}
          </div>
        </section>
      )}

      {!guides?.length && !categories?.length && !popular.length && features.kb && (
        <div className="card empty-state">
          <p>No articles published yet.</p>
          <p className="empty-state-blurb">Your iQuest admin hasn&apos;t published any KB content.</p>
        </div>
      )}
    </div>
  );
}

export default KbBrowse;
